/**
 * @fileoverview The plant check-up ("What's wrong with it?") for a whole plant.
 *
 * The Doctor tile of the plant card's tile row (CardTiles, whose states are usePlantDoctorDoor's in
 * features/plant-doctor/usePlantDoctorAccess; B138) opens a window (PlantDoctor, which the card holds
 * open or shut) that first asks "What are you seeing?" (DoctorSeeing: bugs,
 * holes, spots, yellowing, something else; with bugs, their colour and what
 * they are doing; the choices and their words are the server's), then what
 * they found. Both are required once the choices have loaded, marked the same
 * way; the taps go with the look as facts for the model. That box is required, the same
 * way Add a plant is: Take a look stays pressable, and a blank box is marked
 * in red and given focus rather than quietly blocked. Without a
 * photo it is greyed, and a tap says to add one, as Share does. The server then looks at the plant's
 * history, notes, weather and up to ten of its photos, spread across
 * its life.
 *
 * Questions first (owner, 2026-10-15: "Don't guess at it first… Just ask the
 * questions first"; "the analysis at the very end"): whenever the server
 * holds an answer back (withheld), at the first look or a later round, the
 * window shows only the questions ("A few quick questions first…" before the
 * first ones, "a little more" after), a quiet "Skip, show what you think"
 * (skipFollowUp: the newest held-back answer, final, no AI request) and how
 * many photos were looked at; no likely problem, no summary, no Save to
 * notes. The round that asks nothing more gives the answer below.
 *
 * The window's title and its × sit in a solid bar that stays put while the
 * answer scrolls under it. The answer comes first, in this order:
 *   - the likely problems, from our list, in the words this app wrote for
 *     gardeners: the first "Most likely", the others "Also possible" (how
 *     sure the check-up is only orders them; it is never shown), each headed
 *     by its common name ("Blossom end rot") with its cause under it;
 *   - each one's fuller answer: What it is · Why it happens · What to do now
 *     (in its own box) · Next season · Does it spread? · Is the fruit still
 *     good to eat?, a part with nothing to say left out. The most likely one
 *     shows them all; an "Also possible" one shows what to do now, the rest
 *     folded under "More about it";
 *   - "Why we think so", folded until tapped: You said · Your photos · The
 *     weather, three short lines, the weather's numbers from the record;
 *   - "Narrow it down": the follow-up questions, open, with answers that look
 *     and act like buttons, a count of how many are answered, and "Update the
 *     answer", which waits until every question has one (at most two rounds,
 *     each updating the answer in place);
 *   - its season so far, last and folded ("Its season so far", then "In the newest
 *     photo" only when that line says something the summary does not);
 *   - above that fold, when the newest photo is too old for the forecast (the server says, photoOld),
 *     one plain line saying how old it is and asking for a recent one (written
 *     here, never by the model).
 * When nothing on the list fits, Grow's own suggestion is shown, labelled as
 * such. Every problem is "likely", never a diagnosis; a serious or spreading
 * one adds "Your county extension office can test a sample." A photo that
 * seems to show a different plant gets one quiet line, never the model's
 * commentary.
 *
 * One primary action at a time: "Update the answer" while questions wait,
 * "Save to notes" otherwise; "Start over" is a quiet link.
 *
 * When the newest photo is older than the Grow setting
 * photoai.DOCTOR_STALE_PHOTO_DAYS, the window says so before the check: the
 * check-up judges the plant as it looked then. The check still runs. That,
 * the answer's line asking for a recent photo, and whether the waiting steps
 * say the forecast is checked are the server's, by the check-up's own rules
 * (GET /plant-doctor/status?plantObjId=; audit batch 4, A28), so a sheltered
 * plant is never told its forecast is being checked.
 *
 * Nothing is saved unless the gardener asks: the answer, with their taps and
 * the dates of the photos and weather it looked at, becomes one of the
 * plant's notes with one more tap (the note the server wrote for it).
 *
 * Check-ups are the trial's and Grow More's. On Free the Checkup item (with a
 * lock) still opens the window, which says "Get a check-up whenever something
 * looks wrong · Subscribe" instead of asking (a 402 from the server says the
 * same).
 * A paused plan sees the locked item, which goes to Subscribe.
 *
 * A gardener whose AI the owner switched off (on the Oversight page, whatever
 * their plan) still sees the item, locked, and the window says so calmly
 * with the address to write to (billing.aiRevoked), never Subscribe; a 403
 * AI_REVOKED from the server says the same.
 *
 * The photo help's allowance (D23): a look or a follow-up round refused for
 * it says which limit and when some comes back (AiLimitNotice, "You've
 * reached this month's limit for check-ups…"; a trial's offers Subscribe);
 * an answer given while most of a subscriber's month is used says so once,
 * quietly (AiNearNote). Other refusals are said in the app's words for their
 * code, in the gardener's language (serverMessage), never the server's
 * English. A failure on our side (a 500, or no answer such as a timeout) is the standard sentence
 * (D31, errorMessage): reported to Sentry, with the support address as a
 * link; so is Save to notes failing that way.
 *
 * Small grey text here is the foreground at 80%, not the theme's muted grey,
 * so it passes WCAG AA (4.5:1) on the cream grounds.
 *
 * Where: the plant card (PlantCard): the ⋯ menu's Checkup item and the window,
 * on every card that is not archived.
 * API: GET /plant-doctor/seeing-choices (DoctorSeeing); POST /plant-doctor/:plantObjId
 * { concern, lang, seeing } (diagnosePlant, ?tz=);
 * POST /plant-doctor/checkups/:id/answers (answerFollowUp, the follow-up
 * rounds); POST /plant-doctor/checkups/:id/skip (skipFollowUp); POST /notes/:plantObjId { content, checkupId } (Save to notes; the
 * checkupId lets a Public plant show the checkup on its public pages), then GET
 * /notes/:plantObjId and the plant read again; the photo help's allowance
 * and the check-up status (GET /plant-doctor/status) read again after each.
 * Settings: access.ai and access.aiRevoked (useAiAccess), the plan's state
 * (useBilling: Free sees the subscribe line); the server's
 * limits.PLANT_DOCTOR_CONCERN_MAX_CHARS and limits.NOTE_MAX_LENGTH (lib/growSettings);
 * PLANT_DOCTOR_STATUS_QUERY_KEY (config/tunables/plantList); the photo's
 * status (getDoctorPhotoStatus, while the window is open).
 * Words (en/es): plants.diagnose.* (checkup, ask, locked, needPhoto, title,
 * intro, oldPhoto, concernLabel, concernHint, concernPlaceholder,
 * concernRequired, seeingRequired, seeingBugsRequired, questionsFirst, skipQuestions, start, looking, mostLikely, alsoPossible, whatToDo, part*,
 * moreAbout,
 * evidenceHeading, reason*, narrow*, ownHeading, ownBody, nothingKnown*,
 * extension.{lab,unsureOne,unsureTwo,unknown}, otherPlantPhoto, summaryHeading, seeing, staleNote, saveNote,
 * savedAlready, noted, noteFailed, startOver, photosLooked, caveat,
 * updated, final, freeUsed, subscribe, failed); billing.aiRevoked;
 * serverErrors.AI_LIMIT_* through AiLimitNotice; aiAllowance.near.
 *
 * The answer blocks, the follow-up questions, the access hooks and
 * the text helpers live in features/plant-doctor/ (audit batch 18, A107); this
 * file is the window; the card's Doctor tile (CardTiles) opens it. The
 * answer view is DoctorAnswerView (cleanup C40).
 *
 * @module PlantDoctor
 */

import React, { useContext } from 'react';
import { Stethoscope } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { aiLimitOf, type AiLimitRefusal } from '@/features/billing/lib/aiLimit';
import { refreshAiAllowance } from '@/features/billing/lib/useAiAllowance';
import { AiLimitNotice } from '@/features/billing/components/AiLimitNotice';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import { Button } from '@/shared/components/ui/button';
import { DoctorProgress } from './DoctorProgress';
import { DoctorSeeing, NO_SEEING, seeingComplete, seeingToSend } from './DoctorSeeing';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/shared/components/ui/use-toast';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { PlantsContext } from '@/shared/context/PlantsContextDefinition';
import {
  AI_REVOKED, answerFollowUp, diagnosePlant, diagnosisErrorCode, getDoctorPhotoStatus, PLANT_HAS_NO_PHOTOS, skipFollowUp, SUBSCRIPTION_REQUIRED_AI,
  type PlantDiagnosis,
  type ReportedSeeing,
} from '@/shared/api/diagnose';
import { createNote } from '@/shared/api/notes';
import { useGrowLimits } from '@/shared/lib/growSettings';
import { refreshPlantNotes } from '../../lib/plantNotesCache';
import { PLANT_DOCTOR_STATUS_QUERY_KEY } from '@/config/tunables/plantList';
import { cn } from '@/lib/utils';
import { QUIET } from '@/features/plant-doctor/doctorStyles';
import { usePlantDoctorAccess } from '@/features/plant-doctor/usePlantDoctorAccess';
import { useTendingFor } from '@/features/billing/lib/useBilling';
import { DoctorAnswerView } from '@/features/plant-doctor/DoctorAnswerView';
import { FollowUp } from '@/features/plant-doctor/FollowUp';


/** The plant to look at, and whether its window is open. */
interface PlantDoctorProps {
  /** Whether the window is open; the card holds this, so its ⋯ menu can open it. */
  open: boolean;
  /** Asks to open or close the window (it stays open while a look is on its way). */
  onOpenChange: (open: boolean) => void;
  /** The plant (plant_obj_id). */
  plantObjId: number;
  /** When its newest photo was taken (the card's), shown until the server's own day for it is in. */
  newestPhotoOn: string | null;
  /** The plant's name, for the waiting steps ("Starting a 360° look at your tomato..."). */
  plantName: string;
}

/** The description box, so a blank submit can send focus to it. */
const CONCERN_FIELD_ID = 'plant-doctor-concern';

/** "What are you seeing?", so a submit missing an answer can scroll to it. */
const SEEING_SECTION_ID = 'plant-doctor-seeing';

/**
 * The checkup's window, which the answer comes back in. It opens and closes
 * as the card says (the ⋯ menu's Checkup item), and starts at the question,
 * forgetting any earlier answer, each time it opens.
 *
 * @param props - PlantDoctorProps.
 */
export const PlantDoctor: React.FC<PlantDoctorProps> = ({ open, onOpenChange, plantObjId, newestPhotoOn: cardPhotoOn, plantName }) => {
  const { reactTranslate: t, i18n } = useTranslation();
  const limits = useGrowLimits();
  // The newest photo's status, the server's by the check-up's own rules (A28): read while the window is open.
  const { data: photo } = useQuery({
    queryKey: [...PLANT_DOCTOR_STATUS_QUERY_KEY, 'photo', plantObjId], queryFn: () => getDoctorPhotoStatus(plantObjId), enabled: open,
  });
  const newestPhotoOn = photo?.newestOn ?? cardPhotoOn;
  const stale = Boolean(photo?.photoStale);
  // Too old for the forecast: the answer gets its own plain line asking for a recent photo.
  const showStaleNote = Boolean(photo?.photoOld);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const plantsContext = useContext(PlantsContext);
  // On Free the window offers Grow More instead of a check-up; not when the owner switched AI off.
  const { revoked: aiRevoked, onFree } = usePlantDoctorAccess();
  /** Someone else's garden: their plan is the owner's, so the window never offers Subscribe there (B23). */
  const tending = useTendingFor() !== null;
  const [concern, setConcern] = React.useState('');
  /** What the gardener tapped under "What are you seeing?" (DoctorSeeing). */
  const [seeingPicked, setSeeingPicked] = React.useState<ReportedSeeing>(NO_SEEING);
  /** Whether those choices are shown (and so required); false while loading or when they failed to load. */
  const [seeingLoaded, setSeeingLoaded] = React.useState(false);
  /** True after Take a look was pressed, so a blank description is marked, as Add a plant marks a blank field. */
  const [tried, setTried] = React.useState(false);
  const [looking, setLooking] = React.useState(false);
  const [answer, setAnswer] = React.useState<PlantDiagnosis | null>(null);
  const [narrowing, setNarrowing] = React.useState(false);
  /** Asking for the held-back answer ("Skip, show what you think"). */
  const [skipping, setSkipping] = React.useState(false);
  // True once an answer has arrived and the waiting steps are finishing; progressDone then lets the answer show.
  const [arrived, setArrived] = React.useState(false);
  const progressDone = React.useRef<(() => void) | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  /** The photo help's allowance stopped the last look or round: which limit, and when some comes back. */
  const [limitHit, setLimitHit] = React.useState<AiLimitRefusal | null>(null);
  /** No check-up on this plan (Free): the window offers Grow More instead. */
  const [usedUp, setUsedUp] = React.useState(false);
  /** The server said this account's AI is switched off (403 AI_REVOKED), before the billing status did. */
  const [refusedRevoked, setRefusedRevoked] = React.useState(false);
  /** AI switched off by the owner: the window says so instead of asking. */
  const switchedOff = aiRevoked || refusedRevoked;
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);

  // Each opening starts at the question, forgetting any earlier answer.
  React.useEffect(() => {
    if (!open) return;
    setAnswer(null);
    setTried(false);
    setError(null);
    setLimitHit(null);
    setSaved(false);
    setUsedUp(onFree);
    // Read as the window opens, not on every change while it is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /** Back to the question, keeping what they wrote: "Start over". */
  const startOver = (): void => {
    setAnswer(null);
    setSaved(false);
    setError(null);
    setLimitHit(null);
  };

  /** Reads the check-up status and the week-later questions again after a check-up. */
  const refreshStatus = (): void => {
    void queryClient.invalidateQueries({ queryKey: PLANT_DOCTOR_STATUS_QUERY_KEY });
  };

  /**
   * Words for a refused or failed look.
   *
   * @param err - What the request threw.
   * @returns The message to show; null when the window shows the subscribe line, the
   *   switched-off line or the photo help's limit instead.
   */
  const failure = (err: unknown): string | null => {
    const { code } = diagnosisErrorCode(err);
    const refusal = aiLimitOf(err);
    if (refusal) {
      setLimitHit(refusal);
      return null;
    }
    if (code === AI_REVOKED) {
      setRefusedRevoked(true);
      return null;
    }
    if (code === SUBSCRIPTION_REQUIRED_AI) {
      setUsedUp(true);
      refreshStatus();
      return null;
    }
    if (code === PLANT_HAS_NO_PHOTOS) return t('plants.diagnose.needPhoto');
    // A refusal in the app's words for its code (in the gardener's language), never a 500's log text;
    // a failure on our side, a timeout included (no answer), is the standard sentence, reported (D31).
    return errorMessage(err, t('plants.diagnose.failed'));
  };

  /**
   * Lets the waiting steps finish their ticks, then resolves so the answer can show.
   *
   * @returns A promise that resolves when DoctorProgress calls back.
   */
  const finishProgress = (): Promise<void> => new Promise<void>((resolve) => {
    progressDone.current = resolve;
    setArrived(true);
  });

  /**
   * Sends the plant and what the gardener found.
   *
   * A blank description is marked on the box and given focus, the way Add a
   * plant marks a missing answer, rather than disabling the button.
   */
  const look = async (): Promise<void> => {
    setTried(true);
    const complete = seeingComplete(seeingPicked, seeingLoaded);
    if (!complete.what || !complete.bugs) {
      document.getElementById(SEEING_SECTION_ID)?.scrollIntoView({ block: 'nearest' });
      return;
    }
    if (!concern.trim()) {
      document.getElementById(CONCERN_FIELD_ID)?.focus();
      return;
    }
    setLooking(true);
    setError(null);
    setLimitHit(null);
    try {
      const result = await diagnosePlant(plantObjId, concern, i18n.language, seeingToSend(seeingPicked));
      await finishProgress();
      setAnswer(result);
      refreshAiAllowance(queryClient);
      setSaved(false);
      refreshStatus();
    } catch (err) {
      setError(failure(err));
    } finally {
      setArrived(false);
      setLooking(false);
    }
  };

  /**
   * Sends one round's taps; the updated answer replaces the one shown.
   *
   * @param answers - One tapped option per question.
   */
  const narrow = async (answers: Array<{ id: string; answer: string }>): Promise<void> => {
    if (!answer?.checkupId) return;
    setNarrowing(true);
    setError(null);
    setLimitHit(null);
    try {
      const next = await answerFollowUp(answer.checkupId, answers, i18n.language);
      refreshAiAllowance(queryClient);
      await finishProgress();
      setAnswer(next);
      setSaved(false);
    } catch (err) {
      setError(failure(err));
    } finally {
      setArrived(false);
      setNarrowing(false);
    }
  };

  /**
   * "Skip, show what you think": the first look's held-back answer, final.
   * No AI request; a failure is said as any other (D31).
   */
  const skip = async (): Promise<void> => {
    if (!answer?.checkupId) return;
    setSkipping(true);
    setError(null);
    setLimitHit(null);
    try {
      const next = await skipFollowUp(answer.checkupId);
      setAnswer(next);
      setSaved(false);
      refreshStatus();
    } catch (err) {
      setError(failure(err));
    } finally {
      setSkipping(false);
    }
  };

  /**
   * The answer as a note: the server's own, else what it likely is (or Grow's suggestion), then the summary.
   *
   * @param a - The answer shown.
   * @returns The note, at most the server's limits.NOTE_MAX_LENGTH characters.
   */
  const noteText = (a: PlantDiagnosis): string => {
    if (a.note) return a.note.slice(0, limits.NOTE_MAX_LENGTH);
    /**
     * The evidence after a space, or nothing.
     * @param evidence - Why the check-up thinks so.
     * @returns The text to add.
     */
    const withEvidence = (evidence: string | null): string => (evidence ? ` ${evidence}` : '');
    const parts = a.matches.map((m) => `${m.commonName ?? m.name}.${withEvidence(m.evidence)} ${m.answer.doNow}`);
    if (a.own) parts.push(`${t('plants.diagnose.ownHeading')}: ${a.own.why}.${withEvidence(a.own.evidence)} ${a.own.fix}`);
    if (a.summary) parts.unshift(a.summary);
    return parts.join('\n\n').slice(0, limits.NOTE_MAX_LENGTH);
  };

  /** Saves the answer as one of the plant's notes. */
  const saveNote = async (): Promise<void> => {
    if (!answer) return;
    setSaving(true);
    try {
      // The checkup's id goes with the note, so a Public plant can show the checkup on its public pages.
      await createNote(plantObjId, noteText(answer), answer.checkupId);
      setSaved(true);
      // The card's journal and the Seasons tab each keep a copy of the notes: both get the new one now.
      void refreshPlantNotes(queryClient, plantObjId).catch(() => {});
      void plantsContext?.updateSinglePlant?.(String(plantObjId)).catch(() => {});
      toast({ title: t('plants.diagnose.noted') });
    } catch (err) {
      toast({ title: errorMessage(err, t('plants.diagnose.noteFailed')), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  /** The subscribe line Free sees in place of a check-up (without the Subscribe link in someone else's garden, B23). */
  const subscribeLine = (
    <p className="text-sm">
      {t('plants.diagnose.freeUsed')}
      {!tending && (
        <>
          {' · '}
          <Link to="/subscribe" className="font-semibold underline underline-offset-2">{t('plants.diagnose.subscribe')}</Link>
        </>
      )}
    </p>
  );

  /** What a gardener whose AI the owner switched off is told, with the address to write to. */
  const revokedLine = <p role="note" className="text-sm">{t('billing.aiRevoked')}</p>;

  /** The subscribe or switched-off line, shown under the questions or the answer when one applies. */
  const accessNotice = (
    <>
      {usedUp && !switchedOff && subscribeLine}
      {switchedOff && revokedLine}
    </>
  );

  /** Questions are waiting: "Update the answer" is the one primary action, and Save to notes steps back. */
  const questionsWaiting = Boolean(answer && answer.checkupId !== null && answer.questions.length > 0);

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => { if (!looking && !narrowing && !skipping) onOpenChange(next); }}>
        {/* A solid bar for the title and the ×, and the answer scrolling under it. */}
        <DialogContent
          scrollBody={false}
          className="flex max-w-md flex-col gap-0 overflow-hidden p-0"
        >
          <DialogHeader className="flex min-h-[3.75rem] shrink-0 flex-row items-center space-y-0 border-b bg-background py-3 pl-4 pr-14 text-left">
            <DialogTitle className="font-body">{t('plants.diagnose.title')}</DialogTitle>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {looking ? (
              <DoctorProgress
                kind="first"
                plantName={plantName}
                forecastUsed={Boolean(photo?.forecastUsed)}
                ready={arrived}
                onDone={() => progressDone.current?.()}
              />
            ) : answer?.withheld && questionsWaiting ? (
              // Questions first: no likely problem until they are answered, or skipped.
              <div className="space-y-5">
                {answer.round === 0 && <p className="text-sm font-medium">{t('plants.diagnose.questionsFirst')}</p>}
                <FollowUp
                  key={`${answer.checkupId}-${answer.round}`}
                  questions={answer.questions}
                  again={answer.round > 0}
                  busy={narrowing || skipping}
                  ready={arrived}
                  onProgressDone={() => progressDone.current?.()}
                  plantName={plantName}
                  onSend={(answers) => void narrow(answers)}
                />
                {!narrowing && (
                  <Button
                    type="button"
                    variant="link"
                    loading={skipping}
                    busy="sending"
                    onClick={() => void skip()}
                    className={cn('min-h-11 px-0 text-sm font-medium underline hover:text-foreground', QUIET)}
                  >
                    {t('plants.diagnose.skipQuestions')}
                  </Button>
                )}
                {error && <p role="alert" className="text-sm text-destructive"><FailureText text={error} /></p>}
                {limitHit && <AiLimitNotice refusal={limitHit} />}
                {accessNotice}
                <p className={cn('border-t pt-3 text-xs', QUIET)}>
                  {t('plants.diagnose.photosLooked', { looked: answer.photosLooked, total: answer.photosTotal })}
                </p>
              </div>
            ) : answer ? (
              <DoctorAnswerView
                answer={answer}
                plantName={plantName}
                questionsWaiting={questionsWaiting}
                narrowing={narrowing}
                arrived={arrived}
                onProgressDone={() => progressDone.current?.()}
                onNarrow={(answers) => void narrow(answers)}
                error={error}
                limitHit={limitHit}
                accessNotice={accessNotice}
                showStaleNote={showStaleNote}
                newestPhotoOn={newestPhotoOn}
                saving={saving}
                saved={saved}
                onSave={() => void saveNote()}
                onStartOver={startOver}
              />
            ) : switchedOff ? (
              <div className="space-y-2 py-2">{revokedLine}</div>
            ) : usedUp ? (
              <div className="space-y-2 py-2">{subscribeLine}</div>
            ) : (
              <div className="space-y-3">
                <DialogDescription className={cn('text-xs leading-snug', QUIET)}>{t('plants.diagnose.intro')}</DialogDescription>
                {stale && newestPhotoOn && (
                  <p role="note" className="rounded-md border border-warning/40 bg-warning-tint p-2 text-xs text-warning">
                    {t('plants.diagnose.oldPhoto', {
                      date: new Date(`${newestPhotoOn.slice(0, 10)}T12:00:00`).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' }),
                    })}
                  </p>
                )}
                <div id={SEEING_SECTION_ID}>
                  <DoctorSeeing value={seeingPicked} onChange={setSeeingPicked} tried={tried} onLoaded={setSeeingLoaded} />
                </div>
                <label className="block text-sm font-medium" htmlFor={CONCERN_FIELD_ID}>
                  <span>{t('plants.diagnose.concernLabel')}</span>
                  <span className={cn('mt-0.5 block text-xs font-normal leading-snug', QUIET)}>{t('plants.diagnose.concernHint')}</span>
                  <textarea
                    id={CONCERN_FIELD_ID}
                    value={concern}
                    onChange={(e) => setConcern(e.target.value.slice(0, limits.PLANT_DOCTOR_CONCERN_MAX_CHARS))}
                    maxLength={limits.PLANT_DOCTOR_CONCERN_MAX_CHARS}
                    rows={2}
                    required
                    aria-invalid={tried && !concern.trim() ? true : undefined}
                    placeholder={t('plants.diagnose.concernPlaceholder')}
                    className={cn(
                      'mt-1 w-full rounded-md border bg-card p-2 text-sm font-normal outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      tried && !concern.trim() ? 'border-2 border-destructive' : 'border-input focus:border-primary',
                    )}
                  />
                  {tried && !concern.trim() && (
                    <p className="mt-1 text-sm font-medium text-destructive">{t('plants.diagnose.concernRequired')}</p>
                  )}
                </label>
                {error && <p role="alert" className="text-sm text-destructive"><FailureText text={error} /></p>}
                {limitHit && <AiLimitNotice refusal={limitHit} />}
                <Button onClick={() => void look()} className="min-h-11">
                  <Stethoscope className="mr-2 h-4 w-4" aria-hidden="true" />{t('plants.diagnose.start')}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
